import { useEffect } from "react"
import type { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from "next"
import { useRouter } from "next/router"
import { Layout } from "@components/common"
import { BuilderComponent, Builder, builder } from "@builder.io/react"
import { resolveSwellContent } from "@lib/resolve-swell-content"
import "../../blocks/ProductView/ProductView.builder"
import builderConfig from "@config/builder"
import {
  getAllProductPaths,
  getCollection,
  getProduct,
  getSwellProduct,
} from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import DefaultErrorPage from "next/error"
import Head from "next/head"
import { useThemeUI } from "theme-ui"
import { getLayoutProps } from "@lib/get-layout-props"
import { ProductJsonLd, NextSeo } from "next-seo"
Builder.isStatic = true

const builderModel = "product-page"

export async function getStaticProps(context: GetStaticPropsContext<{ handle: string }>) {
  const product = await getProduct({
    slug: context.params?.handle,
  })
  const swellProduct = await getSwellProduct({
    slug: context.params?.handle,
  })
  const relatedProducts = product?.cross_sells
    ? await Promise.all(
        product.cross_sells.map(async (crossSell: any) => {
          return await getProduct({ id: crossSell.product_id })
        })
      )
    : []
  const page = await resolveSwellContent(builderModel, {
    productHandle: context.params?.handle,
    urlPath: "/product/" + context.params?.handle,
  })

  return {
    props: {
      page: page || null,
      product: product || null,
      swellProduct: swellProduct || null,
      relatedProducts: relatedProducts || null,
      ...(await getLayoutProps()),
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 30 seconds
    revalidate: 30,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const paths = await getAllProductPaths()
  console.log("product paths", paths?.map((path) => `/product/${path}`) ?? [])
  return {
    // TODO: update to /product
    paths: paths?.map((path) => `/product/${path}`) ?? [],
    fallback: "blocking",
  }
}
export default function Handle({
  product,
  swellProduct,
  relatedProducts,
  page,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  Builder.isStatic = true
  const router = useRouter()
  const isLive = !Builder.isEditing && !Builder.isPreviewing
  const { theme } = useThemeUI()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // This includes setting the noindex header because static files always return a status 200 but the rendered not found page page should obviously not be indexed
  if (!product && isLive) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
          <meta name="title"></meta>
        </Head>
        <DefaultErrorPage statusCode={404} />
      </>
    )
  }

  if (router.isFallback && isLive) {
    return (
      <h1>Loading...</h1> // TODO (BC) Add Skeleton Views
    )
  } else if (!product) {
    return (
      <h1>Loading...</h1> // TODO (BC) Add Skeleton Views
    )
  } else {
    return (
      <>
        <ProductJsonLd
          productName={product.name}
          images={product.images.map((image) => image?.src)}
          description={product.meta_description}
          brand="New Way Van Life"
          slogan={product.meta_description}
        />
        <NextSeo
          title={product.name}
          description={product.meta_description}
          openGraph={{
            type: "website",
            title: product.name,
            description: product.meta_description,
            images: product.images.map((image) => {
              return {
                url: image.src,
                width: image.file.width,
                height: image.file.height,
                alt: product.meta_name,
              }
            }),
          }}
        />

        <BuilderComponent
          isStatic
          key={product.id}
          model={builderModel}
          data={{ product, swellProduct, relatedProducts, theme }}
          {...(page && { content: page })}
        />
      </>
    )
  }
}

Handle.Layout = Layout
